import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from "reactjs-popup";
import { toast } from 'react-toastify';
import useIsMobile from '../../shared/hooks/useIsMobile';
import TemplateConfirmDialog from './TemplateConfirmDialog';
import templateErrorMessage from '../utils/templateErrorMessage';
import TemplateMenuItem from './TemplateMenuItem';
import { useV3OfferContext } from '../contexts/V3OfferContext';
import { isPrimaryOffer, canApplyOfferTemplate } from '../utils/lineupOrdering';

const axios = require('axios').default;

const dropdownContentStyle = {
  textAlign: "left",
  border: "1px solid var(--bk-c-e9eaeb, #E9EAEB)",
  borderRadius: "8px",
  boxShadow: "0px 12px 16px -4px rgba(10,13,18,0.08), " +
             "0px 4px 6px -2px rgba(10,13,18,0.03), " +
             "0px 2px 2px -1px rgba(10,13,18,0.04)",
  width: "280px",
  padding: "0px",
  maxHeight: "400px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column"
};

// O-5024: applies an event template to the whole show — its offer template to
// the primary offer, its budget template to the shared budget.
const EventTemplateDropdown = ({
  team,
  csrfToken,
  budget,
  eventTemplates = [],
  onApplyTemplate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const isMobile = useIsMobile();
  // Tolerates a missing V3OfferProvider (bare test renders), same contract as
  // V3OfferOriginCard.
  const ctx = useV3OfferContext();
  const offer = ctx?.offer;
  const openFtuxModal = ctx?.openFtuxModal;

  // Blueprints are OFFER templates, so the per-offer card carries them — but
  // that card is hidden on the primary, and on a single-artist show the primary
  // is the only act there is. That left a solo show able to reach the blueprint
  // library exactly once, from the first-run modal, and never again.
  //
  // Both conditions are load-bearing. isPrimaryOffer: a support already has its
  // own "Change template…" and a second control over one value is what this bar
  // replaced. canApplyOfferTemplate: on a real lineup the server refuses the
  // headliner, so offering it here would only produce an error toast.
  const canBrowseBlueprints = !!openFtuxModal && !!offer &&
    isPrimaryOffer(offer) && canApplyOfferTemplate(offer);

  const currentTemplate = eventTemplates.find(t => t.value === budget.event_template_id);
  const buttonLabel = currentTemplate ? currentTemplate.label : 'Select event template';

  const buttonStyle = {
    background: 'var(--bk-c-ffffff, #ffffff)',
    border: '1px solid var(--bk-c-d5d7da, #D5D7DA)',
    borderRadius: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--bk-c-414651, #414651)',
    boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)'
  };

  const buttonFocusedStyle = {
    ...buttonStyle,
    boxShadow: '0px 0px 0px 2px white, 0px 0px 0px 4px var(--bk-c-2a9ce4, #2A9CE4)'
  };

  const headerStyle = {
    padding: '12px 16px',
    borderBottom: '1px solid var(--bk-c-e9eaeb, #E9EAEB)',
    flexShrink: 0
  };

  const headerTitleStyle = {
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '20px',
    color: 'var(--bk-c-414651, #414651)',
    margin: 0
  };

  const searchInputStyle = {
    marginTop: '8px',
    fontSize: '14px'
  };

  const dividerStyle = {
    height: '1px',
    backgroundColor: 'var(--bk-c-e9eaeb, #E9EAEB)',
    margin: '4px 0'
  };

  const menuItemsContainerStyle = {
    padding: '4px 0',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0
  };

  const filteredTemplates = eventTemplates.filter(template =>
    template.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleApplyTemplate = async () => {
    if (isApplying || !pendingTemplate) return;

    setIsApplying(true);
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.post(
        `/teams/${team.id}/budgets/v3/${budget.id}/apply_event_template`,
        { event_template_id: pendingTemplate.value }
      );

      if (onApplyTemplate) {
        onApplyTemplate(response.data);
      }
      setPendingTemplate(null);
    } catch (error) {
      // A 422 here is a permanent refusal with a reason (recorded deposits,
      // settlement edits) — surfacing "try again" would send the booker in
      // circles.
      toast.error(templateErrorMessage(error, "Couldn't apply that template. Please try again."), {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <Popup
        trigger={open => (
          <button
            type="button"
            style={open ? buttonFocusedStyle : buttonStyle}
            title="Event templates"
            data-testid="event-template-dropdown"
          >
            <span style={{ padding: '0 2px' }}>{buttonLabel}</span>
            <i
              className="fas fa-chevron-down"
              style={{ fontSize: '12px', color: 'var(--bk-c-414651, #414651)' }}
            ></i>
          </button>
        )}
        position={isMobile ? "bottom left" : "bottom right"}
        on="click"
        closeOnDocumentClick
        contentStyle={dropdownContentStyle}
        className="event-template-dropdown"
        arrow={false}
        offsetY={4}
        onClose={() => setSearchTerm('')}
      >
        {close => (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }} role="menu">
            <div style={headerStyle}>
              <p style={headerTitleStyle}>Apply event template</p>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="setlist-input"
                style={searchInputStyle}
                autoFocus
              />
            </div>
            <div style={menuItemsContainerStyle} role="group">
              {filteredTemplates.length === 0 ? (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: 'var(--bk-c-717680, #717680)',
                  fontSize: '14px'
                }}>
                  {searchTerm ? 'No templates found' : 'No templates available'}
                </div>
              ) : (
                filteredTemplates.map(template => (
                  <TemplateMenuItem
                    key={template.value}
                    label={template.label}
                    onClick={() => { setPendingTemplate(template); close(); }}
                    disabled={isApplying}
                  />
                ))
              )}
            </div>
            {canBrowseBlueprints && (
              <>
                <div style={dividerStyle} role="separator"></div>
                <TemplateMenuItem
                  icon="fas fa-layer-group"
                  label="Browse blueprints"
                  subtitle="Curated by Opendate — deal terms only"
                  onClick={() => { close(); openFtuxModal(offer, { nav: 'blueprints' }); }}
                  disabled={isApplying}
                  testId="event-template-browse-blueprints"
                />
              </>
            )}
            <div style={dividerStyle} role="separator"></div>
            <TemplateMenuItem
              icon="fas fa-cog"
              label="Manage event templates"
              onClick={() => {
                close();
                window.open(`/teams/${team.id}/events_details#event-templates`, '_blank', 'noopener');
              }}
              testId="event-template-manage"
            />
          </div>
        )}
      </Popup>

      {/* Applying rebuilds every budget line item — plus the deal brackets,
        * bonuses and deposits — and overwrites the headliner's deal terms, on a
        * budget every artist on the show shares. A multi-artist template also
        * CREATES an offer for each act the show doesn't have yet, with a
        * placeholder artist to replace. Too much to take on a stray click, so
        * the copy names the real blast radius. */}
      <TemplateConfirmDialog
        open={!!pendingTemplate}
        title={`Apply “${pendingTemplate?.label}”?`}
        body={
          'This replaces every line item on this show’s budget — plus its deal '
          + 'brackets, bonuses and deposits — for every artist on the show, and '
          + 'overwrites the headliner’s deal terms. If the template covers more '
          + 'acts than this show has, it adds an offer for each one, with a '
          + 'placeholder artist for you to replace.'
        }
        confirmLabel="Apply template"
        busyLabel="Applying..."
        busy={isApplying}
        destructive
        onConfirm={handleApplyTemplate}
        onCancel={() => setPendingTemplate(null)}
        testId="event-template-apply-confirm"
      />
    </>
  );
};

EventTemplateDropdown.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  budget: PropTypes.object.isRequired,
  eventTemplates: PropTypes.array,
  onApplyTemplate: PropTypes.func
};

export default EventTemplateDropdown;
